/* Free-gift email pop-up. Pure rules first (unit-tested from tools/popup), the page controller at the bottom. */
export const SKIP_TEMPLATES = ['cart', 'password', 'gift_card'];
const DAY = 86400000;

export function decide(s) {
  if (!s.enabled || s.designMode || s.done || s.subscriber || s.openedThisLoad) return 'skip';
  const t = String(s.template || '');
  if (SKIP_TEMPLATES.includes(t) || t.startsWith('customers')) return 'skip';
  if (s.closedAt && s.now - s.closedAt < s.closeDays * DAY) return 'skip';
  if (s.overlayOpen) return 'wait';
  if (s.lastBuyClickAt && s.now - s.lastBuyClickAt < 10000) return 'wait';
  if (s.pointerFine && s.wide) return s.exitIntent && s.secondsOnPage >= 5 ? 'open' : 'wait';
  return s.scrollPct >= 40 || s.secondsOnPage >= 20 ? 'open' : 'wait';
}

export function afterLoad(qs, pending, now, formErrors, anyErrors) {
  if (formErrors) return 'error';
  if (qs && qs.get('sfai_gift') === 'sent') return 'thankyou';
  if (pending && typeof pending.at === 'number' && now - pending.at < 120000) return anyErrors ? 'error' : 'thankyou';
  return null;
}

export function thankYouLinks(roles, slug) {
  const main = roles.find((r) => r.slug === slug) || null;
  return { main, others: roles.filter((r) => r.slug !== slug) };
}

export function safeStorage(ls) {
  const mem = new Map();
  const fromMem = (k) => (mem.has(k) ? mem.get(k) : null);
  return {
    get(k) { try { const v = ls.getItem(k); return v === null ? fromMem(k) : v; } catch { return fromMem(k); } },
    set(k, v) { mem.set(k, v); try { ls.setItem(k, v); } catch { /* blocked storage: memory only */ } },
    del(k) { mem.delete(k); try { ls.removeItem(k); } catch { /* same */ } },
  };
}

const BUY_SELECTOR = 'a[data-sfai-buy], button[name="add"], a[href*="/checkout"]';
const OVERLAY_SELECTOR = 'dialog[open]:not(.sfai-gift__dialog)'; // the cart drawer and search modal are both <dialog>

function init() {
  const cfgEl = document.getElementById('sfai-gift-config');
  const host = document.getElementById('sfai-gift');
  if (!cfgEl || !host) return;
  const cfg = JSON.parse(cfgEl.textContent);
  let ls = null;
  try { ls = window.localStorage; } catch { ls = null; }
  const store = safeStorage(ls);
  const form = host.querySelector('form');
  const pill = document.querySelector('[data-gift-pill]');
  const t0 = Date.now();
  let scrollPct = 0, exitIntent = false, lastBuyClickAt = 0, openedThisLoad = false, submitted = false, timer = 0;

  const plural = (slug) => (cfg.roles.find((r) => r.slug === slug) || {}).plural || '';
  const roleOf = () => cfg.role || (form.querySelector('input[name="sfai_role"]:checked') || {}).value || '';

  function setState(name) {
    host.querySelectorAll('[data-state]').forEach((el) => { el.hidden = el.dataset.state !== name; });
  }
  function fillForm() {
    const known = host.querySelector('[data-body-known]');
    const pickerBody = host.querySelector('[data-body-picker]');
    const picker = host.querySelector('[data-picker]');
    const isKnown = Boolean(cfg.role);
    known.textContent = known.dataset.template.replace('{job}', plural(cfg.role));
    known.hidden = !isKnown;
    pickerBody.hidden = isKnown;
    picker.hidden = isKnown;
  }
  function fillThanks(slug) {
    const { main, others } = thankYouLinks(cfg.roles, slug);
    const a = host.querySelector('[data-download]');
    host.querySelector('[data-thanks-known]').hidden = !main;
    host.querySelector('[data-thanks-none]').hidden = Boolean(main);
    host.querySelector('[data-others-prefix]').hidden = !main;
    if (main) { a.href = main.pdf; a.hidden = false; } else { a.hidden = true; }
    host.querySelector('[data-others]').replaceChildren(...others.flatMap((r, i) => {
      const link = document.createElement('a');
      link.href = r.pdf; link.target = '_blank'; link.rel = 'noopener'; link.textContent = r.label;
      return i ? [document.createTextNode(', '), link] : [link];
    }));
  }
  async function open(state) {
    await customElements.whenDefined('dialog-component');
    openedThisLoad = true;
    if (timer) clearInterval(timer);
    setState(state);
    host.showDialog();
  }

  // QA overrides, then the return from a submit.
  const qs = new URLSearchParams(location.search);
  const qa = qs.get('sfai_gift');
  let pending = null;
  try { pending = JSON.parse(store.get('sfai_gift_pending') || 'null'); } catch { pending = null; }
  const back = afterLoad(qs, pending, Date.now(), cfg.formErrors, cfg.anyErrors);
  if (qa === 'open') { fillForm(); open('form'); }
  else if (qa === 'thanks') { fillThanks(qs.get('role') || ''); open('thanks'); }
  else if (back === 'thankyou') { store.del('sfai_gift_pending'); store.set('sfai_gift_done', '1'); fillThanks(qs.get('role') || (pending && pending.role) || ''); open('thanks'); }
  else if (back === 'error') { store.del('sfai_gift_pending'); fillForm(); host.querySelector('[data-error]').hidden = false; open('form'); }

  // The pill: anyone can open the form on purpose.
  if (pill && !store.get('sfai_gift_done') && !cfg.designMode) {
    pill.hidden = false;
    pill.addEventListener('click', () => { fillForm(); open('form'); });
  }

  form.addEventListener('submit', () => {
    const slug = roleOf();
    form.querySelector('[data-tags]').value = slug ? `gift-prompts,prompts-${slug}` : 'gift-prompts';
    const ret = new URLSearchParams(location.search);
    ret.set('sfai_gift', 'sent'); ret.set('role', slug);
    form.querySelector('[data-return]').value = location.pathname + '?' + ret.toString();
    store.set('sfai_gift_pending', JSON.stringify({ role: slug, at: Date.now() }));
    submitted = true;
    (window.dataLayer = window.dataLayer || []).push({ event: 'generate_lead_stape', lead_type: 'gift-prompts', role: slug || 'none', placement: 'popup' });
  });
  host.addEventListener('dialog:close', () => {
    if (!submitted && !store.get('sfai_gift_done')) store.set('sfai_gift_closed_at', String(Date.now()));
  });

  // Signals for the rules.
  document.addEventListener('scroll', () => {
    const h = document.documentElement;
    const max = h.scrollHeight - h.clientHeight;
    if (max > 0) scrollPct = Math.max(scrollPct, Math.round((100 * h.scrollTop) / max));
    tick();
  }, { passive: true });
  document.addEventListener('mouseout', (e) => { if (!e.relatedTarget && e.clientY <= 0) { exitIntent = true; tick(); } });
  document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest(BUY_SELECTOR)) lastBuyClickAt = Date.now(); }, true);

  function snapshot() {
    return {
      enabled: cfg.enabled, designMode: cfg.designMode, template: cfg.template, subscriber: cfg.subscriber,
      done: Boolean(store.get('sfai_gift_done')), closedAt: Number(store.get('sfai_gift_closed_at')) || 0, closeDays: cfg.closeDays,
      now: Date.now(), openedThisLoad, overlayOpen: Boolean(document.querySelector(OVERLAY_SELECTOR)), lastBuyClickAt,
      pointerFine: matchMedia('(pointer: fine)').matches, wide: matchMedia('(min-width: 750px)').matches,
      secondsOnPage: (Date.now() - t0) / 1000, scrollPct, exitIntent,
    };
  }
  function tick() {
    if (openedThisLoad) return;
    const d = decide(snapshot());
    if (d === 'open') { fillForm(); open('form'); }
    else if (d === 'skip' && timer) { clearInterval(timer); timer = 0; }
  }
  if (!qa || qa === 'sent') {
    timer = setInterval(tick, 1000);
    setTimeout(() => { if (timer) { clearInterval(timer); timer = 0; } }, 90000);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}
